
import React, { useState, useEffect } from 'react';
import { Calendar } from '@/components/ui/calendar';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import DashboardLayout from '@/components/dashboard/DashboardLayout';
import { Button } from '@/components/ui/button';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { Calendar as CalendarIcon, UserPlus, PlusCircle, List, ArchiveIcon } from 'lucide-react';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { toast } from '@/components/ui/use-toast';
import { Skeleton } from '@/components/ui/skeleton';
import { getAppointments } from '@/services/AppointmentService';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';

export default function Agenda() {
  const [date, setDate] = useState<Date | undefined>(new Date());
  const [showArchived, setShowArchived] = useState(false);

  const {
    data: appointmentsResult,
    isLoading,
    refetch
  } = useQuery({
    queryKey: ['appointments', date ? format(date, 'yyyy-MM-dd') : '', showArchived],
    queryFn: () => getAppointments(showArchived, date),
  });

  const appointments = appointmentsResult?.data || [];

  const formattedDate = date 
    ? format(date, "EEEE, d 'de' MMMM 'de' yyyy", { locale: ptBR }) 
    : '';

  return (
    <DashboardLayout title="Agenda Jurídica" isLoading={isLoading}>
      <div className="space-y-6">
        <div className="flex justify-end items-center">
          <div className="flex gap-2">
            <Button asChild className='flex items-center gap-2 bg-[#072a45] hover:bg-[#072a45] dark:bg-[#d4af37] dark:hover:bg-[#d4af37] text-white dark:text-[#072a45]'>
              <Link to="/dashboard/appointments">
                <PlusCircle className="mr-2 h-4 w-4" />
                Novo Compromisso
              </Link>
            </Button>
            <Button variant="outline" asChild className='dark:text-white'>
              <Link to="/dashboard/clients">
                <UserPlus className="mr-2 h-4 w-4" />
                Novo Cliente
              </Link>
            </Button>
          </div>
        </div>

        <div className="flex items-center space-x-2">
          <Switch
            id="show-archived"
            checked={showArchived}
            onCheckedChange={setShowArchived}
          />
          <Label htmlFor="show-archived" className='text-sm text-gray-700'>Mostrar compromissos arquivados</Label>
        </div>

        <Tabs defaultValue="calendar" className="w-full">
          <TabsList>
            <TabsTrigger value="calendar">
              <CalendarIcon className="mr-2 h-4 w-4" />
              Calendário
            </TabsTrigger>
            <TabsTrigger value="list">
              <List className="mr-2 h-4 w-4" />
              Lista
            </TabsTrigger>
          </TabsList>
          <TabsContent value="calendar" className="mt-4">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <Card className="col-span-1 dark:bg-white">
                <CardContent className="pt-6">
                  <Calendar
                    mode="single"
                    selected={date}
                    onSelect={setDate}
                    className="pointer-events-auto dark:text-gray-600"
                    classNames={{
                        day_selected:'!bg-[#072a45] text-white !hover:bg-[#072a45] hover:text-white', 
                    }}
                    locale={ptBR}
                  />
                </CardContent>
              </Card>
              <Card className="col-span-1 md:col-span-2 dark:bg-white">
                <CardHeader>
                  <CardTitle className="flex items-center capitalize dark:text-gray-600">
                    <CalendarIcon className="mr-2 h-5 w-5" />
                    {formattedDate}
                  </CardTitle>
                  <CardDescription>
                    Compromissos agendados para esta data
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  {isLoading ? (
                    <div className="space-y-4">
                      <Skeleton className="h-20 w-full" />
                      <Skeleton className="h-20 w-full" />
                    </div>
                  ) : appointments && appointments.length > 0 ? (
                    <div className="space-y-4">
                      {appointments.map((appointment) => {
                        const startDate = new Date(appointment.date);
                        const endDate = appointment.end_date ? new Date(appointment.end_date) : null;
                        const clientName = appointment.clients?.name;
                        
                        return (
                          <div 
                            key={appointment.id} 
                            className={`border rounded-lg p-4 hover:bg-gray-50 dark:hover:bg-gray-800 ${
                              appointment.archived ? 'bg-gray-100 dark:bg-gray-800/50 opacity-70' : ''
                            }`}
                          >
                            <div className="flex justify-between">
                              <h3 className="font-medium dark:text-gray-600">{appointment.title}</h3>
                              <span className="text-sm bg-lawyer-primary/10 text-lawyer-primary px-2 py-1 rounded-full">
                                {appointment.type}
                              </span>
                            </div>
                            <div className="text-sm text-gray-500 mt-1">
                              Cliente: {clientName || 'Sem cliente associado'}
                            </div>
                            <div className="text-sm mt-2 dark:text-gray-600">
                              {format(startDate, 'HH:mm')} {endDate && `- ${format(endDate, 'HH:mm')}`}
                            </div>
                            {appointment.notes && (
                              <div className="text-sm mt-2 text-gray-600">
                                {appointment.notes}
                              </div>
                            )}
                            {appointment.archived && (
                              <div className="mt-2 flex items-center text-xs text-gray-500">
                                <ArchiveIcon className="h-3 w-3 mr-1" />
                                Arquivado
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  ) : (
                    <div className="text-center py-6 text-gray-500">
                      Nenhum compromisso agendado para esta data.
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>
          </TabsContent>
          <TabsContent value="list">
            <Card className='dark:bg-white'>
              <CardHeader>
                <CardTitle>Todos os compromissos</CardTitle>
                <CardDescription>
                  Visualize seus compromissos em formato de lista
                </CardDescription>
              </CardHeader>
              <CardContent>
                {isLoading ? (
                  <div className="space-y-4">
                    <Skeleton className="h-20 w-full" />
                    <Skeleton className="h-20 w-full" />
                  </div>
                ) : appointments && appointments.length > 0 ? (
                  <div className="space-y-4">
                    {appointments.map((appointment) => {
                      const appointmentDate = new Date(appointment.date);
                      const clientName = appointment.clients?.name;
                      
                      return (
                        <div 
                          key={appointment.id} 
                          className={`border rounded-lg p-4 hover:bg-gray-50 dark:hover:bg-gray-800 ${
                            appointment.archived ? 'bg-gray-100 dark:bg-gray-800/50 opacity-70' : ''
                          }`}
                        >
                          <div className="flex justify-between">
                            <h3 className="font-medium">{appointment.title}</h3>
                            <span className="text-sm bg-lawyer-primary/10 text-lawyer-primary px-2 py-1 rounded-full">
                              {appointment.type}
                            </span>
                          </div>
                          <div className="flex justify-between mt-2">
                            <div className="text-sm">
                              <span className="font-medium">Data: </span>
                              {format(appointmentDate, "dd/MM/yyyy 'às' HH:mm", { locale: ptBR })}
                            </div>
                            <div className="text-sm text-gray-500">
                              Cliente: {clientName || 'Sem cliente associado'}
                            </div>
                          </div>
                          {appointment.archived && (
                            <div className="mt-2 flex items-center text-xs text-gray-500">
                              <ArchiveIcon className="h-3 w-3 mr-1" />
                              Arquivado
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  <div className="text-center py-6 text-gray-500">
                    Nenhum compromisso encontrado.
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
    </DashboardLayout>
  );
}
